/**
 * Per-entity date-format setting (ORG_entity_settings.date_format) — the single
 * source of truth for how read-only dates render across the app.
 *
 * The value is cached in localStorage so the very first paint after a reload
 * already uses the entity's format, then refreshed from the backend once the
 * session is authenticated (AuthContext) and whenever Entity Settings saves.
 *
 * Display helpers (FormControl.formatDate, DatePicker default format) read the
 * module-level cache synchronously — no context/provider needed.
 */
import { getEntitySettings } from '../services/orgService';

export type EntityDateFormat = 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';

const STORAGE_KEY = 'entity_date_format';
const DEFAULT_FORMAT: EntityDateFormat = 'DD-MM-YYYY';

const isValidFormat = (v: unknown): v is EntityDateFormat =>
  v === 'DD-MM-YYYY' || v === 'MM-DD-YYYY' || v === 'YYYY-MM-DD';

const readStored = (): EntityDateFormat => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isValidFormat(v) ? v : DEFAULT_FORMAT;
  } catch {
    return DEFAULT_FORMAT;
  }
};

let current: EntityDateFormat = readStored();

export const getEntityDateFormat = (): EntityDateFormat => current;

export const setEntityDateFormat = (fmt: string | null | undefined): void => {
  if (!isValidFormat(fmt) || fmt === current) return;
  current = fmt;
  try { window.localStorage.setItem(STORAGE_KEY, fmt); } catch { /* private mode */ }
};

/**
 * Fetch the entity settings and update the cached format. Fire-and-forget:
 * on any failure the cached/default format stays in effect.
 */
export const refreshEntityDateFormat = async (): Promise<void> => {
  try {
    const s = await getEntitySettings();
    setEntityDateFormat(s?.date_format);
  } catch { /* keep cached/default */ }
};

/** react-datepicker format string matching the entity setting. */
export const toDatePickerFormat = (): string => {
  switch (current) {
    case 'MM-DD-YYYY': return 'MM-dd-yyyy';
    case 'YYYY-MM-DD': return 'yyyy-MM-dd';
    default: return 'dd-MM-yyyy';
  }
};

/** Input placeholder matching the entity setting (lowercase convention). */
export const toDatePlaceholder = (): string => current.toLowerCase();

/**
 * Format an ISO date ("YYYY-MM-DD", optionally with a time part) into the
 * entity's configured display format. Returns '—' for empty/unparseable input.
 */
export const formatDateBySetting = (value?: string | null): string => {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!m) return '—';
  const [, y, mo, d] = m;
  switch (current) {
    case 'MM-DD-YYYY': return `${mo}-${d}-${y}`;
    case 'YYYY-MM-DD': return `${y}-${mo}-${d}`;
    default: return `${d}-${mo}-${y}`;
  }
};
